import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { type DashboardData } from '../store/types';
import styles from './cpCapacityDataGrid.module.scss';

// ---- MonthGrid helper component (internal, not exported) --------------------

interface MonthGridRow {
  label: string;
  values: (string | number)[];
  className?: string;
}

interface MonthGridProps {
  months: string[];
  header: string;
  icon: string;
  badge?: string;
  rows: MonthGridRow[];
}

function fmtMonth(iso: string): string {
  const [year, month] = iso.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
}

function fmt(value: string | number): string {
  if (typeof value === 'number') return value.toFixed(1);
  return value;
}

function MonthGrid({ months, header, icon, badge, rows }: MonthGridProps) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3>
          <i className={icon} />
          {header}
          {badge && <span className={styles.recommendBadge}>{badge}</span>}
        </h3>
      </div>
      <div className={styles.gridWrapper}>
        <table className={styles.grid}>
          <thead>
            <tr>
              <th>Row</th>
              {months.map((m) => (
                <th key={m}>{fmtMonth(m)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className={row.className ? styles[row.className as keyof typeof styles] : undefined}>
                <td>{row.label}</td>
                {row.values.map((v, i) => (
                  // Using row label + index — values are positional per month, index is stable here
                  <td key={`${row.label}-${i}`}>{fmt(v)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- peak-value helper (for recommend badges) --------------------------------

function peakAcrossMonths(data: DashboardData, key: 'packaging_total' | 'quality_total'): number {
  const months = data.months;
  let peak = 0;
  months.forEach((m) => {
    const val = data.hc_breakdown[m]?.[key] ?? 0;
    if (val > peak) peak = val;
  });
  return Math.ceil(peak);
}

// ---- main component ---------------------------------------------------------

const CPCapacityDataGrid = observer(() => {
  const store = useCapacityStore();
  const { dashboardData } = store;

  if (!dashboardData) {
    return (
      <div className={styles.section} style={{ padding: 24 }}>
        <span style={{ color: '#94a3b8', fontSize: 13 }}>No data available</span>
      </div>
    );
  }

  const months = store.filteredMonths;
  const { crewing_factors, capacity_by_room_type, hc_breakdown, changeover_data } = dashboardData;

  // ---- 1. Crewing Factors --------------------------------------------------

  const crewingRows: MonthGridRow[] = [
    {
      label: 'Working Days',
      values: months.map((m) => crewing_factors[m]?.work_days ?? 0),
    },
    {
      label: 'Forecast Bottles',
      values: months.map((m) => {
        const v = crewing_factors[m]?.forecast_bottles ?? 0;
        return typeof v === 'number' ? Math.round(v) : v;
      }),
    },
    {
      label: 'Bottles / Wkday',
      values: months.map((m) => crewing_factors[m]?.bottles_per_wkday ?? 0),
    },
  ];

  // ---- 2. Capacity by Room Type --------------------------------------------

  const roomTypeKeys = Object.keys(capacity_by_room_type);
  const capacityRows: MonthGridRow[] = roomTypeKeys.map((cat) => ({
    label: cat,
    values: months.map((m) => capacity_by_room_type[cat]?.[m] ?? 0),
  }));

  // ---- 3. Packaging FTE Summary -------------------------------------------

  const peakPkg = peakAcrossMonths(dashboardData, 'packaging_total');

  const packagingRows: MonthGridRow[] = [
    {
      label: 'PA (Direct)',
      values: months.map((m) => hc_breakdown[m]?.pa ?? 0),
    },
    {
      label: 'PA Lead',
      values: months.map((m) => hc_breakdown[m]?.pa_lead ?? 0),
      className: 'rowFixed',
    },
    {
      label: 'MO (Direct)',
      values: months.map((m) => hc_breakdown[m]?.mo ?? 0),
    },
    {
      label: 'MO Lead',
      values: months.map((m) => hc_breakdown[m]?.mo_lead ?? 0),
      className: 'rowFixed',
    },
    {
      label: 'Aggregation',
      values: months.map((m) => hc_breakdown[m]?.aggregation ?? 0),
      className: 'rowFixed',
    },
    {
      label: 'Shift Coverage',
      values: months.map((m) => hc_breakdown[m]?.shift_coverage ?? 0),
      className: 'rowFixed',
    },
    {
      label: 'Packaging Total',
      values: months.map((m) => hc_breakdown[m]?.packaging_total ?? 0),
      className: 'rowTotal',
    },
  ];

  // ---- 4. Quality FTE Summary ---------------------------------------------

  const peakQty = peakAcrossMonths(dashboardData, 'quality_total');

  const qualityRows: MonthGridRow[] = [
    {
      label: 'QI (Direct)',
      values: months.map((m) => hc_breakdown[m]?.qi ?? 0),
    },
    {
      label: 'QI Lead',
      values: months.map((m) => hc_breakdown[m]?.qi_lead ?? 0),
      className: 'rowFixed',
    },
    {
      label: 'QLS',
      values: months.map((m) => hc_breakdown[m]?.qls ?? 0),
      className: 'rowFixed',
    },
    {
      label: 'Quality Total',
      values: months.map((m) => hc_breakdown[m]?.quality_total ?? 0),
      className: 'rowTotal',
    },
  ];

  // ---- 5. Changeover Hours — expanded to individual room types -------------

  const CAT_ROOM_TYPES: Record<string, string[]> = {
    HIGH: ['High Speed 1', 'High Speed 2', 'Medium Speed'],
    LOW:  ['Low Speed 1', 'Low Speed 2'],
    OVER: ['Overlay'],
  };

  const ROOM_TYPE_ORDER = [
    'High Speed 1', 'High Speed 2', 'Medium Speed',
    'Low Speed 1', 'Low Speed 2', 'Overlay',
  ];

  const changeoverRows: MonthGridRow[] = [];
  ROOM_TYPE_ORDER.forEach((rt) => {
    const cat = Object.keys(CAT_ROOM_TYPES).find((c) => CAT_ROOM_TYPES[c].includes(rt));
    if (!cat) return;
    const catRoomTypes = CAT_ROOM_TYPES[cat];
    changeoverRows.push({
      label: rt,
      values: months.map((m) => {
        const catHrs = changeover_data[cat]?.[m] ?? 0;
        if (catRoomTypes.length === 1) return catHrs;
        const totalLineHrs = catRoomTypes.reduce(
          (s, r) => s + (capacity_by_room_type[r]?.[m] ?? 0), 0,
        );
        const rtLineHrs = capacity_by_room_type[rt]?.[m] ?? 0;
        return totalLineHrs > 0
          ? Math.round((catHrs * (rtLineHrs / totalLineHrs)) * 10) / 10
          : Math.round((catHrs / catRoomTypes.length) * 10) / 10;
      }),
    });
  });

  changeoverRows.push({
    label: 'Total CO Hours',
    values: months.map((m) =>
      Object.values(changeover_data).reduce((sum, byMonth) => sum + (byMonth[m] ?? 0), 0),
    ),
    className: 'rowSubtotal',
  });

  return (
    <div>
      <MonthGrid
        months={months}
        header="Crewing Factors"
        icon="pi pi-calendar"
        rows={crewingRows}
      />

      <MonthGrid
        months={months}
        header="Capacity by Room Type"
        icon="pi pi-building"
        rows={capacityRows}
      />

      <MonthGrid
        months={months}
        header="Packaging FTE Summary"
        icon="pi pi-users"
        badge={`Peak: ${peakPkg} FTE`}
        rows={packagingRows}
      />

      <MonthGrid
        months={months}
        header="Quality FTE Summary"
        icon="pi pi-check-circle"
        badge={`Peak: ${peakQty} FTE`}
        rows={qualityRows}
      />

      <MonthGrid
        months={months}
        header="Changeover Hours"
        icon="pi pi-refresh"
        rows={changeoverRows}
      />
    </div>
  );
});

export default CPCapacityDataGrid;
